import { useQuery } from '@tanstack/react-query';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { humanize } from '../../lib/enums';

/**
 * The readings the stock screens share.
 *
 * Every one of these is cached under the same key the tables and pickers already use, so a figure
 * that is on screen once costs nothing to read a second time — the waste form, the count sheet and
 * the on-hand table all want the same three lists.
 */

export const STOCK_PATH = '/admin/v1/inventory/stock';
export const AREAS_PATH = '/admin/v1/inventory/storage-locations';
export const LEDGER_PATH = '/admin/v1/inventory/ledger';
export const ITEMS_PATH = '/admin/v1/inventory/items';
export const RECIPES_PATH = '/admin/v1/inventory/recipes';
export const REASONS_PATH = '/admin/v1/admin/reason-codes';
export const REORDER_PATH = '/admin/v1/inventory/reorder-worklist';
/** Counts taken; with `?waiting=true`, the ones holding their area shut until they are dealt with. */
export const COUNTS_PATH = '/admin/v1/inventory/stock-counts';
export const FX_RATES_PATH = '/admin/v1/pricing/fx-rates';
/** Who sells one stock item, ranked by what it costs once the crossing is paid. */
export const landedCostPath = (inventoryItemId: string, fxRateMicro: number) =>
  `/admin/v1/suppliers/landed-cost/${inventoryItemId}?fxRateMicro=${fxRateMicro}`;

export interface StockItem {
  id: string;
  nameEn: string;
  stockUnit: string;
  avgCostBaseMinor: number;
  parLevel: number | null;
  reorderPoint: number | null;
}

export interface StockItemIndex {
  byId: Map<string, StockItem>;
  /**
   * The unit an item is counted in.
   *
   * Waste, moves and counts all have to send a unit, and the only honest answer is the unit the
   * item is already counted in — asking a manager to type "bottle" next to a number he has just
   * entered is an invitation to type "bottles" once and split the item's history in two.
   */
  unitOf: (id: unknown) => string;
  /** What one unit has been costing, for valuing what is on a shelf. */
  costOf: (id: unknown) => number;
  nameOf: (id: unknown) => string;
}

export function useStockItems(): StockItemIndex {
  const { data } = useQuery({
    queryKey: listKey(ITEMS_PATH),
    queryFn: () => rawApi<Record<string, unknown>[]>('GET', ITEMS_PATH),
    staleTime: 60_000,
  });

  const byId = new Map<string, StockItem>(
    (data ?? []).map((r) => [
      String(r.id),
      {
        id: String(r.id),
        nameEn: String(r.nameEn ?? ''),
        stockUnit: String(r.stockUnit ?? ''),
        avgCostBaseMinor: typeof r.avgCostBaseMinor === 'number' ? r.avgCostBaseMinor : 0,
        parLevel: typeof r.parLevel === 'number' ? r.parLevel : null,
        reorderPoint: typeof r.reorderPoint === 'number' ? r.reorderPoint : null,
      },
    ]),
  );

  const get = (id: unknown) => (id ? byId.get(String(id)) : undefined);
  return {
    byId,
    unitOf: (id) => get(id)?.stockUnit ?? '',
    costOf: (id) => get(id)?.avgCostBaseMinor ?? 0,
    nameOf: (id) => get(id)?.nameEn ?? '',
  };
}

export interface StorageAreaRow {
  id: string;
  name: string;
  storageRoomId: string | null;
  roomName: string | null;
  /** Shelf, fridge, deep freezer — worded through the enum registry wherever it is shown. */
  kind: string;
  active: boolean;
}

/**
 * The storage areas as rows rather than as picker options, for the screens that need the room a
 * place is in and not only its name. Same cache entry every picker already reads, so the rooms
 * table showing what is inside each room costs no extra request.
 */
export function useStorageAreas() {
  const { data, isLoading, isError } = useQuery({
    queryKey: listKey(AREAS_PATH),
    queryFn: () => rawApi<Record<string, unknown>[]>('GET', AREAS_PATH),
    staleTime: 60_000,
  });
  const rows: StorageAreaRow[] = (data ?? []).map((r) => ({
    id: String(r.id),
    name: String(r.name ?? ''),
    storageRoomId: r.storageRoomId ? String(r.storageRoomId) : null,
    roomName: r.roomName ? String(r.roomName) : null,
    kind: String(r.kind ?? ''),
    active: r.active !== false,
  }));
  return { rows, isLoading, isError };
}

export interface StockOnHandRow {
  inventoryItemId: string;
  storageLocationId: string;
  qtyOnHand: number;
  updatedAt?: string | null;
}

/**
 * What the books say is on the shelf — for one area, for one item, or for everything.
 *
 * The two narrow readings are the two questions actually asked of this table. A count sheet wants
 * one area across every item; a stock item's own record wants one item across every area, which
 * used to mean pulling the whole table down and throwing all but four rows away.
 */
export function useStockOnHand(storageLocationId?: string, inventoryItemId?: string) {
  const query: Record<string, string> = {};
  if (storageLocationId) query.storageLocationId = storageLocationId;
  if (inventoryItemId) query.inventoryItemId = inventoryItemId;
  const search = new URLSearchParams(query).toString();
  const { data, isLoading } = useQuery({
    queryKey: listKey(STOCK_PATH, Object.keys(query).length > 0 ? query : undefined),
    queryFn: () => rawApi<StockOnHandRow[]>('GET', search ? `${STOCK_PATH}?${search}` : STOCK_PATH),
  });
  return { rows: data ?? [], isLoading };
}

/**
 * The exchange rate the bar last recorded.
 *
 * Comparing a dollar price against a lempira one needs a rate, and there is exactly one place the
 * portal should get it: what somebody entered on the pricing screen. Never a constant in a page —
 * the rate moves, and a stale copy makes the mainland vendor look cheaper than he is.
 */
export function useCurrentFxRateMicro(): number | undefined {
  const { data } = useQuery({
    queryKey: listKey(FX_RATES_PATH),
    queryFn: () => rawApi<{ rateMicro: number }[]>('GET', FX_RATES_PATH),
    staleTime: 60_000,
  });
  // The endpoint answers newest first.
  const rate = data?.[0]?.rateMicro;
  return typeof rate === 'number' && rate > 0 ? rate : undefined;
}

/**
 * Waste reason names by code.
 *
 * Scoped to the waste taxonomy on purpose. The reason a movement carries is only ever the one
 * recorded against the waste behind it, and the same short code is allowed to exist under a second
 * taxonomy with a different meaning — the table is unique on (kind, code), not on code. Resolving
 * across all of them would let a bottle thrown away read back with the wording of a register
 * correction, and a wrong name is worse than no name because nobody checks it twice.
 *
 * A code with no name behind it is worded rather than printed: `SPOILED` on a screen a manager
 * reads is a database talking, and every other coded column in the portal humanises the same way.
 */
export function useReasonNames(): (code: unknown) => string {
  const { data } = useQuery({
    queryKey: listKey(REASONS_PATH),
    queryFn: () => rawApi<Record<string, unknown>[]>('GET', REASONS_PATH),
    staleTime: 60_000,
  });
  const byCode = new Map(
    (data ?? []).filter((r) => r.kind === 'waste').map((r) => [String(r.code), String(r.nameEn ?? '')]),
  );
  return (code: unknown) => {
    if (code === null || code === undefined || code === '') return '';
    return byCode.get(String(code)) || humanize(String(code));
  };
}

export const UNIT_CONVERSIONS_PATH = '/admin/v1/inventory/unit-conversions';
export const COUNTABLE_UNITS_PATH = '/admin/v1/inventory/countable-units';

/**
 * Which units an amount of a given item may be written in — on a recipe, on a waste slip, or on a
 * count sheet.
 *
 * Nobody writes a recipe in cases, and nobody counts a bar in them either. The rum is bought by the
 * case, held in bottles and poured in millilitres; the man with the clipboard is holding bottles. A
 * form that offers only the unit the shelf is counted in makes the person at the keyboard do the
 * division — which they will do once, correctly, and then never again.
 *
 * **This used to walk the conversion rows itself**, and that walk was a second copy of `units.ts` —
 * bounded hops, both directions, item-scoped rows shadowing global ones — living on the other side
 * of the wire from the one that decides whether an amount is accepted. Two copies of a rule
 * disagree within a year, and this pair would have disagreed silently in both directions: offering
 * a unit the API then refused, or withholding one that was perfectly usable. The API answers it now
 * from the same `UnitTable` the refusal is made with, so a unit is on this list exactly when an
 * amount written in it will be taken. `stockUnit` is the fallback for an item the list has not
 * loaded yet or does not know, and it is always usable.
 */
export function useUnitOptions(): (inventoryItemId: string, stockUnit: string) => string[] {
  const { data } = useQuery({
    queryKey: listKey(COUNTABLE_UNITS_PATH),
    queryFn: () => rawApi<{ inventoryItemId: string; units: string[] }[]>('GET', COUNTABLE_UNITS_PATH),
    // Deliberately not held for a minute like its neighbours. This list is DERIVED from the
    // conversions, and the screen that edits those invalidates the conversions' own cache entry and
    // knows nothing about this one — so a manager who adds "1 case = 24 bottles" and opens the
    // count sheet would be offered the old set. It is a handful of rows and every screen that wants
    // it mounts fresh when it opens, so refetching on mount costs one small request and removes a
    // whole class of "I just added that and it is not on the list".
  });
  const byItem = new Map((data ?? []).map((r) => [r.inventoryItemId, r.units]));

  return (inventoryItemId, stockUnit) => {
    const units = byItem.get(inventoryItemId);
    if (units && units.length > 0) return units;
    return stockUnit ? [stockUnit] : [];
  };
}

export interface RecipeRow {
  id: string;
  name: string;
  /** `modifierOption` is an extra someone adds or leaves off — "Add bacon", "No cheese". */
  targetType: 'variant' | 'inventoryItem' | 'modifierOption';
  targetId: string;
  yieldQty: number | null;
  yieldUnit: string | null;
  costBaseMinor: number | null;
  /**
   * How many of its ingredients have never been bought and so were costed at nothing. Zero means
   * the cost is whole; anything else means it is a floor and the margin beside it is flattering.
   */
  uncostedComponents?: number | null;
  /** What a guest is charged for one, in the money the price list is written in. */
  sellsForMinor?: number | null;
  sellsForCurrency?: string | null;
  /** The same price in the money the books are kept in, which is the money the cost is in. */
  sellsForBaseMinor?: number | null;
  /** Gross margin in basis points. Worked out on the API, where both figures are in one currency. */
  marginBp?: number | null;
}

/**
 * The margin as a person would say it, and how worried to be about it.
 *
 * Wording, not arithmetic: the figure itself comes from the API, because the price and the cost are
 * often in different money and subtracting them on a screen is a twenty-six-fold error that looks
 * perfectly reasonable. Sixty-five per cent on a plate of food is healthy; under a third is not,
 * and negative means the dish is sold for less than the ingredients cost.
 */
export function marginTone(marginBp: number | null | undefined): string | undefined {
  if (typeof marginBp !== 'number') return undefined;
  if (marginBp < 0) return 'red.7';
  if (marginBp < 3000) return 'orange.7';
  return undefined;
}

/** The active recipes, for the list itself and for picking one as an ingredient of another. */
export function useRecipes() {
  const { data, isLoading } = useQuery({
    queryKey: listKey(RECIPES_PATH),
    queryFn: () => rawApi<RecipeRow[]>('GET', RECIPES_PATH),
    staleTime: 60_000,
  });
  const rows = data ?? [];
  const byId = new Map(rows.map((r) => [r.id, r]));
  return { rows, byId, isLoading };
}
